import { addMinutes } from "date-fns";
import { ReactNode } from "react";
import { atom, useAtomValue, useSetAtom } from "jotai";

import {
  CustomerSendMailFlag,
  ReservationApiPutReservationOrderRequest,
  ReservationApiPostReservationPaymentRequest,
  ServiceReservationPeriodFlag,
  ServiceReservationSpecifiedPeriodFlag,
  ServiceReserveStatus,
  ServiceStatus,
  OrderSettlementMethod,
  CustomerReservationCalendarServiceDto,
  ReservationApiPutReservationCalendarOrderRequest,
  ReservationApiPostReservationCalendarPaymentRequest,
  ServicePaidFlag,
  UserSpecialCommercialLaw,
  FormCustomFormCustomFormsOptionsInner,
  ServiceFormsType,
  ServiceViewTermFlag,
  ServiceViewType,
  UserPlan,
} from "@/apis/clients";
import { serviceFormAtom } from "@/states/service-form/atoms";
import {
  FormOptions,
  HoldDate,
  ServiceFormCustomerData,
  ServiceFormCustomerInputs,
  ServiceFormData,
  ServiceFormServiceInputs,
  ServiceFormType,
  ServiceFormUiState,
  ServiceInfo,
  ServiceFormAtom,
  ServiceFormErrors,
  FormPayment,
} from "@/states/service-form/types";
import {
  buildCouponContextKey,
  buildPricingSummary,
  hasAppliedCouponForContext,
} from "@/states/service-form/pricing";
import { assertNever } from "@/utils/common";
import {
  CUSTOM_OTHER_FIELD_ID,
  GENDER_OPTIONS,
  INDUSTRY_OTHER_ID,
  PREF_OPTIONS,
} from "@/utils/const";
import { format } from "@/utils/date";
import { convertServiceMenuTimeRequired } from "@/utils/enum-converter";
import { getCustomFieldKey, getIdsFormCustomKey } from "./operations";
import { IFileUpload } from "@/components/organisms/service-form/CustomFormUploadFile";
import { useCallback } from "react";

const serviceFormUiStateAtom = atom<ServiceFormUiState>((get) => {
  const {
    isLoading,
    formId,
    pageId,
    formData,
    inputs,
    errors,
    options,
    payment,
    paymentMethod,
    clientSecret,
    plan,
    draft,
    isAgreePolicy,
  } = get(serviceFormAtom);

  if (!formId) {
    return {
      status: "NotLoading",
    };
  }

  if (isLoading) {
    return {
      status: "Loading",
      formId,
    };
  }

//  if (formData.customer.length === 0) {
//    return {
//      status: "LoadingFailed",
//      formId,
//    };
//  }

  return {
    status: "Loaded",
    formId,
    pageId,
    formData,
    inputs,
    errors,
    options,
    payment,
    paymentMethod,
    clientSecret,
    plan,
    draft,
    isAgreePolicy,
  };
});

export const useServiceFormUiState = (): ServiceFormUiState =>
  useAtomValue(serviceFormUiStateAtom);

export const useServiceFormAtom = () => useAtomValue(serviceFormAtom);

// Jotaiでのreset実装
const initialServiceFormAtom: ServiceFormAtom = {
  isLoading: false,
  formId: undefined,
  pageId: undefined,
  formData: {
    service: {
      id: 0,
      type: "content-webinar",
      viewType: ServiceViewType.BUTTON,
      name: "",
      specialCommercialLaw: UserSpecialCommercialLaw.INVALID,
      options: [],
      reservation_specified_period_flag:
        ServiceReservationSpecifiedPeriodFlag.UNSPECIFIED,
      viewTermFlag: ServiceViewTermFlag.HIDE,
    },
    holdDates: [],
    customer: [],
  },
  inputs: {
    service: {
      detailIds: [],
      optionIds: [],
      serviceMenuId: undefined,
      serviceMenuOptionIds: [],
      serviceMenuReservationDateTime: undefined,
      quantity: {},
    },
    customer: {
      mailMagazine: false,
    },
  },
  errors: {
    service: {},
    customer: {},
  } as ServiceFormErrors,
  options: {
    industries: [],
  },
  payment: {},
  paymentMethod: {},
  clientSecret: undefined,
  plan: UserPlan.FREE,
  draft: {
    inputs: {
      service: {
        detailIds: [],
        optionIds: [],
        serviceMenuId: undefined,
        serviceMenuOptionIds: [],
        serviceMenuReservationDateTime: undefined,
        quantity: {},
      },
      customer: {
        mailMagazine: false,
      },
    },
    errors: {
      service: {},
      customer: {},
    } as ServiceFormErrors,
  },
  isAgreePolicy: false,
};

export const useResetServiceFormUiState = () => {
  const setServiceForm = useSetAtom(serviceFormAtom);
  return useCallback(() => {
    setServiceForm(initialServiceFormAtom);
  }, [setServiceForm]);
};

const serviceInfoAtom = atom<ServiceInfo | undefined>((get) => {
  const uiState = get(serviceFormUiStateAtom);
  if (uiState.status !== "Loaded") {
    return undefined;
  }

  return uiState.formData.service;
});

export const useServiceInfo = (): ServiceInfo | undefined =>
  useAtomValue(serviceInfoAtom);

const holdDatesAtom = atom<HoldDate[]>((get) => {
  const uiState = get(serviceFormUiStateAtom);
  if (uiState.status !== "Loaded") {
    return [];
  }

  return uiState.formData.holdDates;
});

export const useHoldDates = (): HoldDate[] => useAtomValue(holdDatesAtom);

const formDataAtom = atom<ServiceFormData>((get) => {
  const uiState = get(serviceFormUiStateAtom);
  if (uiState.status !== "Loaded") {
    return {} as ServiceFormData; // Return an empty object as a placeholder
  }

  return uiState.formData;
});

export const useFormData = (): ServiceFormData => useAtomValue(formDataAtom);

export interface ConfirmServiceInfo {
  id: number | undefined;
  name: string;
  contactName: string;
  details: {
    id: number;
    eventStartAt: Date | undefined;
    eventEndAt: Date | undefined;
    placeName: string;
    price: number | undefined;
    type: number;
    description?: string;
    remainingReservationCount?: number;
    end?: Date;
    start?: Date;
  }[];
  options: {
    id: number;
    name: string;
    price: number | undefined;
    time?: number | undefined;
  }[];
  serviceMenu?: {
    id: number;
    name: string;
    price: number;
  };
  settlementMethod: OrderSettlementMethod;
  specialCommercialLaw?: UserSpecialCommercialLaw;
  serviceMenuId?: number;
  serviceMenuReservationDateTime?: Date;
  serviceMenuOptionIds?: number[];
  quantity?: { [key: string]: number };
}

interface ConfirmInputList {
  service: ConfirmServiceInfo;
  serviceDraft?: ConfirmServiceInfo;
  customer: {
    title: string;
    text?: string | ReactNode;
    files?: IFileUpload[];
    type?: ServiceFormsType;
  }[];
}

const confirmCustomerInputListAtom = atom<ConfirmInputList>((get) => {
  const pageFormUiState = get(serviceFormUiStateAtom);
  if (pageFormUiState.status !== "Loaded") {
    return {
      service: {
        id: undefined,
        name: "",
        contactName: "",
        options: [],
        details: [],
        serviceMenu: undefined,
        settlementMethod: OrderSettlementMethod.FREE,
        specialCommercialLaw: UserSpecialCommercialLaw.INVALID,
        serviceMenuId: undefined,
        serviceMenuOptionIds: [],
        quantity: {},
      },
      customer: [],
    };
  }

  const serviceInfo = pageFormUiState.formData.service;
  const serviceInputs = pageFormUiState.inputs.service;
  const customerInputs: Partial<ServiceFormCustomerInputs> =
    pageFormUiState.inputs.customer;
  const customerFormData = pageFormUiState.formData.customer;
  const options = pageFormUiState.options;
  const service = makeConfirmServiceInfo(serviceInfo, serviceInputs);

  const serviceInputsDraft = pageFormUiState?.draft
    ? pageFormUiState.draft.inputs.service
    : serviceInputs;
  const serviceDraft = makeConfirmServiceInfo(serviceInfo, serviceInputsDraft);

  return {
    serviceDraft,
    service,
    customer: customerFormData.map((form) => {
      const text = getText(form, customerInputs, options);
      return {
        title: getTitle(form),
        text: text === "" ? "-" : text,
        type: form.type,
        files:
          form.type === ServiceFormsType.UPLOAD ? customerInputs.files : [],
      };
    }),
  };
});

const makeConfirmServiceInfo = (
  serviceInfo: ServiceInfo,
  serviceInputs: ServiceFormServiceInputs,
): ConfirmServiceInfo => {
  if (serviceInfo.type === "event-seminar" || serviceInfo.type === "form") {
    return {
      id: serviceInfo.id,
      name: serviceInfo.name,
      contactName: serviceInfo.contactName,
      details: serviceInfo.details.filter((detail) =>
        serviceInputs.detailIds.includes(detail.id),
      ),
      options: serviceInfo.options.filter((option) =>
        serviceInputs.optionIds.includes(option.id),
      ),
      settlementMethod: serviceInfo.paid_flag
        ? OrderSettlementMethod.CREDIT
        : OrderSettlementMethod.FREE,
      specialCommercialLaw: serviceInfo.specialCommercialLaw,
      serviceMenuId: serviceInputs.serviceMenuId,
      serviceMenuOptionIds: serviceInputs.serviceMenuOptionIds,
      serviceMenuReservationDateTime:
        serviceInputs.serviceMenuReservationDateTime,
      quantity: serviceInputs.quantity,
    };
  }

  if (serviceInfo.type === "calendar") {
    const calendarDetail = serviceInfo.details[0];
    const serviceMenu = serviceInfo.menus.find(
      (menu) => menu.id === serviceInputs.serviceMenuId,
    );

    if (serviceMenu === undefined) {
      return {
        id: serviceInfo.id,
        name: serviceInfo.name,
        contactName: serviceInfo.contactName,
        details: [],
        options: [],
        serviceMenu,
        // todo カレンダーを有料に対応させる
        settlementMethod: OrderSettlementMethod.FREE,
        specialCommercialLaw: serviceInfo.specialCommercialLaw,
      };
      // throw new Error("サービスメニューが選択されていません。");
    }
    if (serviceInputs.serviceMenuReservationDateTime === undefined) {
      return {
        serviceMenuId: serviceMenu.id,
        serviceMenuOptionIds: serviceInputs.serviceMenuOptionIds,
        id: serviceInfo.id,
        name: serviceInfo.name,
        contactName: serviceInfo.contactName,
        details: [],
        options: [],
        serviceMenu,
        // todo カレンダーを有料に対応させる
        settlementMethod: OrderSettlementMethod.FREE,
        specialCommercialLaw: serviceInfo.specialCommercialLaw,
      };
    }
    const details: ConfirmInputList["service"]["details"] = [
      {
        id: calendarDetail.id,
        placeName: calendarDetail.placeName || "",
        price: serviceMenu.price,
        type: calendarDetail.type,
        eventStartAt: serviceInputs.serviceMenuReservationDateTime,
        eventEndAt: addMinutes(
          serviceInputs.serviceMenuReservationDateTime,
          convertServiceMenuTimeRequired(serviceMenu.timeRequired),
        ),
      },
    ];
    const options = serviceMenu.options
      .filter(
        (option) =>
          serviceInputs.serviceMenuOptionIds &&
          serviceInputs.serviceMenuOptionIds.includes(option.id),
      )
      .map((option) => ({
        id: option.id,
        name: option.name,
        price: option.amount,
        time: option.time,
      }));

    return {
      id: serviceInfo.id,
      name: serviceInfo.name,
      contactName: serviceInfo.contactName,
      details,
      options,
      serviceMenu,
      // todo カレンダーを有料に対応させる
      settlementMethod: OrderSettlementMethod.FREE,
      specialCommercialLaw: serviceInfo.specialCommercialLaw,
      serviceMenuId: serviceInputs.serviceMenuId,
      serviceMenuReservationDateTime:
        serviceInputs.serviceMenuReservationDateTime,
      serviceMenuOptionIds: serviceInputs.serviceMenuOptionIds,
      quantity: serviceInputs.quantity,
    };
  }

  if (serviceInfo.type === "subscription") {
    return {
      id: serviceInfo.id,
      name: serviceInfo.name,
      contactName: serviceInfo.contactName,
      details: serviceInfo.details
        .filter((detail) => serviceInputs.detailIds.includes(detail.id))
        .map((detail) => ({
          id: detail.id,
          price: detail.price,
          type: 0,
          eventStartAt: undefined,
          eventEndAt: undefined,
          placeName: "",
          name: detail.name,
          description: detail.description,
          contractPeriod: detail.contractPeriod,
          isSetContractPeriod: detail.isSetContractPeriod,
          freeTrialPeriod: detail.freeTrialPeriod,
          isSetFreeTrialPeriod: detail.isSetFreeTrialPeriod,
        })),
      options: [],
      settlementMethod: serviceInfo.paid_flag
        ? OrderSettlementMethod.CREDIT
        : OrderSettlementMethod.FREE,
      specialCommercialLaw: serviceInfo.specialCommercialLaw,
      serviceMenuId: serviceInputs.serviceMenuId,
      serviceMenuOptionIds: serviceInputs.serviceMenuOptionIds,
      serviceMenuReservationDateTime:
        serviceInputs.serviceMenuReservationDateTime,
      quantity: serviceInputs.quantity,
    };
  }

  if (
    serviceInfo.type === "content-webinar" &&
    serviceInfo.paid_flag === ServicePaidFlag.PAID
  ) {
    return {
      id: serviceInfo.id,
      name: serviceInfo.name,
      contactName: "",
      details: [],
      options: [],
      settlementMethod: serviceInfo.paid_flag
        ? OrderSettlementMethod.CREDIT
        : OrderSettlementMethod.FREE,
      specialCommercialLaw: serviceInfo.specialCommercialLaw,
    };
  }

  return {
    id: serviceInfo.id,
    name: serviceInfo.name,
    contactName: "",
    details: [],
    options: [],
    settlementMethod: OrderSettlementMethod.FREE,
    specialCommercialLaw: serviceInfo.specialCommercialLaw,
  };
};

const getTitle = (formData: ServiceFormCustomerData) => {
  if (formData.type === ServiceFormType.ADDRESS1) {
    return "市区町村以降の住所";
  }
  if (formData.customForm.label) {
    return formData.customForm.label;
  }
  return titles[formData.type] || "";
};

const getText = (
  serviceFormData: ServiceFormCustomerData,
  inputs: Partial<ServiceFormCustomerInputs>,
  options: FormOptions,
): string => {
  switch (serviceFormData.type) {
    case ServiceFormType.QUESTIONNAIRE:
      return inputs.questionnaire || "";
    case ServiceFormType.NAME:
      return (inputs.lastName || "") + " " + (inputs.firstName || "");
    case ServiceFormType.NAME_KANA:
      return (inputs.lastNameKana || "") + " " + (inputs.firstNameKana || "");
    case ServiceFormType.MAIL_ADDRESS:
      return inputs.email || "";
    case ServiceFormType.MAIL_DELIVERY:
      return inputs.mailMagazine ? "最新のお知らせメールを受け取る" : "お知らせメールを受け取らない";
    case ServiceFormType.TEL:
      return inputs.tel || "";
    case ServiceFormType.ZIP:
      return inputs.zip1 || "";
    case ServiceFormType.PREFECTURE:
      // eslint-disable-next-line no-case-declarations
      const pref = PREF_OPTIONS.find(
        (item) => item.value === inputs.prefectureId?.toString(),
      );
      return pref ? pref.text : "";
    case ServiceFormType.ADDRESS1:
      return inputs.address1 || "";
    case ServiceFormType.ADDRESS2:
      return inputs.address2 || "";
    case ServiceFormType.BIRTHDAY:
      return inputs.birthday || "";
    case ServiceFormType.AGE:
      return inputs.age?.toString() || "";
    case ServiceFormType.GENDER:
      // eslint-disable-next-line no-case-declarations
      const gender = GENDER_OPTIONS.find(
        (item) => item.value === inputs.gender,
      );
      return gender ? gender.text : "";
    case ServiceFormType.LINE_ID:
      return inputs.lineId || "";
    case ServiceFormType.FACEBOOK_ID:
      return inputs.facebookId || "";
    case ServiceFormType.TWITTER_ID:
      return inputs.twitterId || "";
    case ServiceFormType.INSTAGRAM_ID:
      return inputs.instagramId || "";
    case ServiceFormType.COMPANY_NAME:
      return inputs.companyName || "";
    case ServiceFormType.INDUSTRY:
      {
        if (inputs.industryId === INDUSTRY_OTHER_ID) {
          return `その他（${inputs.industryOther || ""}）`;
        }
      }
      // eslint-disable-next-line no-case-declarations
      const industry = options.industries.find(
        (item) => item.value == inputs.industryId,
      );
      return industry ? industry.text : "";
    case ServiceFormType.POSITION:
      return inputs.position || "";
    case ServiceFormType.COMPANY_HP:
      return inputs.companyHp || "";
    case ServiceFormType.DEPARTMENT_NAME:
      return inputs.department || "";

    case ServiceFormType.SINGLE_LINE_DESCRIPTION:
    case ServiceFormType.MULTI_LINE_DESCRIPTION: {
      const customKey = getCustomFieldKey(
        serviceFormData.type,
        serviceFormData.serviceFormId,
        serviceFormData.customForm.serviceCustomFormId,
      );
      const value = inputs[customKey] as string;
      return value;
    }
    case ServiceFormType.OPTIONS: {
      const customKey = getCustomFieldKey(
        serviceFormData.type,
        serviceFormData.serviceFormId,
        serviceFormData.customForm.serviceCustomFormId,
      );
      const values = (
        inputs[customKey] as FormCustomFormCustomFormsOptionsInner[]
      )
        ?.map((option) =>
          option.otherLabel
            ? `その他 (${option.otherLabel || ""})`
            : `${option.label}`,
        )
        .join(", ");
      return values;
    }

    default:
      return "実装されていません";
  }
};

const titles = {
  [ServiceFormType.QUESTIONNAIRE]: "アンケート",
  [ServiceFormType.NAME]: "氏名",
  [ServiceFormType.NAME_KANA]: "フリガナ",
  [ServiceFormType.MAIL_ADDRESS]: "メールアドレス",
  [ServiceFormType.MAIL_DELIVERY]: "メルマガ配信",
  [ServiceFormType.TEL]: "電話番号",
  [ServiceFormType.ZIP]: "郵便番号",
  [ServiceFormType.PREFECTURE]: "都道府県名",
  [ServiceFormType.ADDRESS1]: "住所",
  [ServiceFormType.ADDRESS2]: "住所",
  [ServiceFormType.BIRTHDAY]: "生年月日",
  [ServiceFormType.AGE]: "年齢",
  [ServiceFormType.GENDER]: "性別",
  [ServiceFormType.LINE_ID]: "LINE ID",
  [ServiceFormType.FACEBOOK_ID]: "Facebook ID",
  [ServiceFormType.TWITTER_ID]: "Twitter ID",
  [ServiceFormType.INSTAGRAM_ID]: "Instagram ID",
  [ServiceFormType.COMPANY_NAME]: "会社名",
  [ServiceFormType.INDUSTRY]: "業種",
  [ServiceFormType.POSITION]: "役職",
  [ServiceFormType.COMPANY_HP]: "会社ホームページ",
  [ServiceFormType.DEPARTMENT_NAME]: "部署名",
  [ServiceFormType.SINGLE_LINE_DESCRIPTION]: "自由記述（複数行）",
  [ServiceFormType.MULTI_LINE_DESCRIPTION]: "自由記述（複数行）",
  [ServiceFormType.OPTIONS]: "選択式",
  [ServiceFormType.UPLOAD]: "ファイルアップロード",
};

const getSelectedPaymentMethodAtom = atom((get) => {
  const formUiState = get(serviceFormUiStateAtom);
  if (formUiState.status !== "Loaded") {
    return undefined;
  }

  const payment = resolvePaymentState(formUiState);

  return {
    payment_intent_id: payment.paymentIntentId || "",
    payment_paypal_id: payment?.paymentPayPalId || "",
    billing_amount: payment.billingAmount ?? 0,
    settlement_method: payment.settlementMethod,
    coupon_id: payment.couponId,
    coupon_code: payment.couponCode,
  };
});

export const useGetSelectedPaymentMethod = () =>
  useAtomValue(getSelectedPaymentMethodAtom);

export const useConfirmInputList = (): ConfirmInputList =>
  useAtomValue(confirmCustomerInputListAtom);

const pricingSummaryAtom = atom((get) => {
  const formUiState = get(serviceFormUiStateAtom);
  if (formUiState.status !== "Loaded") {
    return buildPricingSummary(undefined, undefined, undefined);
  }

  return buildPricingSummary(
    formUiState.formData.service,
    formUiState.inputs.service,
    formUiState.payment,
  );
});

export const usePricingSummary = () => useAtomValue(pricingSummaryAtom);

const putReservationOrderRequestAtom = atom<
  | ReservationApiPutReservationOrderRequest
  | ReservationApiPutReservationCalendarOrderRequest
  | undefined
>((get) => {
  const formUiState = get(serviceFormUiStateAtom);
  if (formUiState.status !== "Loaded") {
    return undefined;
  }

  const payment = resolvePaymentState(formUiState);

  if (formUiState.formData.service.type === "calendar") {
    const calendarReq: ReservationApiPutReservationCalendarOrderRequest = {
      putReservationCalendarOrderRequest: {
        service: makeCalendarServicePayload(
          formUiState.formData.service,
          formUiState.inputs.service,
        ),
        customer: makeCustomerPayload(formUiState.inputs.customer),
        payment: {
          payment_intent_id: payment.paymentIntentId || "",
          payment_paypal_id: payment?.paymentPayPalId || "",
          billing_amount: payment.billingAmount ?? 0,
          settlement_method: payment.settlementMethod,
          coupon_id: payment.couponId,
          coupon_code: payment.couponCode,
        },
        page_id: formUiState.pageId,
      },
    };
    return calendarReq;
  } else {
    const servicePayload = makeServicePayload(
      formUiState.formData.service,
      formUiState.inputs.service,
    );
    // For subscription services, inject the Stripe subscription ID captured at payment time
    if (
      formUiState.formData.service.type === "subscription" &&
      formUiState.payment.subscriptionId
    ) {
      (servicePayload as Record<string, unknown>).stripe_subscription_id =
        formUiState.payment.subscriptionId;
    }
    const req: ReservationApiPutReservationOrderRequest = {
      putReservationOrderRequest: {
        service: servicePayload,
        customer: makeCustomerPayload(formUiState.inputs.customer),
        payment: {
          payment_intent_id: payment.paymentIntentId || "",
          payment_paypal_id: payment?.paymentPayPalId || "",
          billing_amount: payment.billingAmount ?? 0,
          settlement_method: payment.settlementMethod,
          coupon_id: payment.couponId,
          coupon_code: payment.couponCode,
        },
        page_id: formUiState.pageId,
      },
    };
    return req;
  }
});

export const usePutReservationOrderRequest = ():
  | ReservationApiPutReservationOrderRequest
  | ReservationApiPutReservationCalendarOrderRequest
  | undefined => useAtomValue(putReservationOrderRequestAtom);

const postReservationPaymentRequestAtom = atom<
  | ReservationApiPostReservationPaymentRequest
  | ReservationApiPostReservationCalendarPaymentRequest
  | undefined
>((get) => {
  const formUiState = get(serviceFormUiStateAtom);
  if (formUiState.status !== "Loaded") {
    return undefined;
  }

  const payment = resolvePaymentState(formUiState);

  if (formUiState.formData.service.type === "calendar") {
    const req: ReservationApiPostReservationCalendarPaymentRequest = {
      postReservationCalendarPaymentRequest: {
        service: makeCalendarServicePayload(
          formUiState.formData.service,
          formUiState.inputs.service,
        ),
        customer: makeCustomerPayload(formUiState.inputs.customer),
        payment_method_id: formUiState.paymentMethod?.paymentMethodId || "",
        coupon_id: payment.couponId,
        coupon_code: payment.couponCode,
      },
    };

    return req;
  } else {
    const req: ReservationApiPostReservationPaymentRequest = {
      postReservationPaymentRequest: {
        service: makeServicePayload(
          formUiState.formData.service,
          formUiState.inputs.service,
        ),
        customer: makeCustomerPayload(formUiState.inputs.customer),
        payment_method_id: formUiState.paymentMethod?.paymentMethodId || "",
        coupon_id: payment.couponId,
        coupon_code: payment.couponCode,
      },
    };

    return req;
  }
});

export const usePostReservationPaymentRequest = ():
  | ReservationApiPostReservationPaymentRequest
  | ReservationApiPostReservationCalendarPaymentRequest
  | undefined => useAtomValue(postReservationPaymentRequestAtom);

/**
 * 予約APIのペイロードのサービス情報部分を生成（カレンダー以外）
 * @param service
 * @param serviceInputs
 */
const makeServicePayload = (
  service: ServiceInfo,
  serviceInputs: ServiceFormServiceInputs,
) => {
  if (service.type === "calendar") {
    throw new Error("カレンダータイプは予約APIが異なります");
  }

  let detailIds: number[] = [];
  let service_details: { id: number; quantity: number }[] = [];

  const isEventSeminar = service.type === "event-seminar";
  const isForm = service.type === "form";

  if (isEventSeminar || isForm) {
    if ("details" in service) {
      const reserveStatus = service.reserveStatus;
      switch (reserveStatus) {
        case ServiceReserveStatus.ALL:
          // ここはapiの呼び出し時に初期値セットが理想だが、現状apiのコールをたくさんしているためここで暫定対応
          detailIds = service.details.map((detail) => detail.id);
          break;
        case ServiceReserveStatus.SINGLE_SELECT:
        case ServiceReserveStatus.MULTIPLE_SELECT:
          detailIds = serviceInputs.detailIds;
          break;
        default:
          // return [];
          assertNever(reserveStatus);
      }
    }

    if (isEventSeminar) {
      const quantity = serviceInputs.quantity;
      if (quantity) {
        // Build service_details as array of { id, quantity }
        service_details = Object.entries(quantity)
          .filter(([idStr]) => detailIds.includes(Number(idStr)))
          .map(([idStr, qty]) => ({ id: Number(idStr), quantity: qty }));
      }
    }
  } else if (service.type === "subscription") {
    // Subscriptions: Use the specific menu selected by the user.
    // We must prioritize serviceInputs.detailIds to ensure the user's choice is respected,
    // rather than accidentally defaulting to the first item in the list.
    if (serviceInputs.detailIds.length > 0) {
      detailIds = serviceInputs.detailIds;
    } else if (
      "details" in service &&
      service.details.length > 0 &&
      service.details[0].id
    ) {
      detailIds = [service.details[0].id]; // fallback: auto-selected single menu
    }
  } else {
    if ("details" in service) {
      if (
        service.details &&
        service.details.length > 0 &&
        service.details[0].id
      ) {
        detailIds = [service.details[0].id];
      }
    }
  }

  const optionIds =
    serviceInputs.optionIds.length > 0 ? serviceInputs.optionIds : undefined;

  return {
    id: service.id,
    service_detail_ids: detailIds,
    service_option_ids: optionIds,
    ...(service.type === "subscription" && detailIds.length > 0
      ? { service_menu_id: detailIds[0] }
      : {}),
    ...(isEventSeminar && service_details.length > 0
      ? {
          service_details: service_details,
        }
      : {}),
  };
};

const resolvePaymentState = (
  formUiState: Extract<ServiceFormUiState, { status: "Loaded" }>,
): FormPayment => {
  const pricingSummary = buildPricingSummary(
    formUiState.formData.service,
    formUiState.inputs.service,
    formUiState.payment,
  );
  const currentContextKey = buildCouponContextKey(
    formUiState.formData.service,
    formUiState.inputs.service,
  );
  const hasCoupon = hasAppliedCouponForContext(
    formUiState.payment,
    currentContextKey,
  );

  return {
    ...formUiState.payment,
    billingAmount: pricingSummary.finalAmount,
    settlementAmount: pricingSummary.settlementAmount,
    couponId: hasCoupon ? formUiState.payment.couponId : undefined,
    couponCode: hasCoupon ? formUiState.payment.couponCode : undefined,
    couponDiscountAmount: hasCoupon
      ? formUiState.payment.couponDiscountAmount
      : undefined,
    couponEligibleSubtotal: hasCoupon
      ? formUiState.payment.couponEligibleSubtotal
      : undefined,
    couponLabel: hasCoupon ? formUiState.payment.couponLabel : undefined,
    couponContextKey: hasCoupon
      ? formUiState.payment.couponContextKey
      : undefined,
  };
};

/**
 * カレンダーサービス向け予約APIのペイロードのサービス情報部分を生成
 * @param service
 * @param serviceInputs
 */
const makeCalendarServicePayload = (
  service: ServiceInfo,
  serviceInputs: ServiceFormServiceInputs,
): CustomerReservationCalendarServiceDto => {
  if (service.type !== "calendar") {
    throw new Error("カレンダータイプ以外は予約APIが異なります");
  }

  if (!serviceInputs?.serviceMenuId) {
    return {
      id: service.id,
      service_menu: {
        id: 0,
        reservation_datetime: "",
        service_menu_option_ids: [],
      },
    };
    // throw new Error("サービスメニューが選択されていません。");
  }
  if (!serviceInputs?.serviceMenuReservationDateTime) {
    return {
      id: service.id,
      service_menu: {
        id: serviceInputs.serviceMenuId,
        reservation_datetime: "",
        service_menu_option_ids: [],
      },
    };
    // throw new Error("予約日時が選択されていません。");
  }

  return {
    id: service.id,
    service_menu: {
      id: serviceInputs.serviceMenuId,
      reservation_datetime: format(
        serviceInputs.serviceMenuReservationDateTime,
        "yyy-MM-dd'T'HH:mm:ss",
      ),
      service_menu_option_ids: serviceInputs.serviceMenuOptionIds,
    },
  };
};

/**
 * 予約APIのペイロードのお客様情報部分を生成
 * @param customerInputs
 */
const makeCustomerPayload = (
  customerInputs: Partial<ServiceFormCustomerInputs>,
) => {
  const custom_form_options = [];
  for (const [key, value] of Object.entries(customerInputs)) {
    const fieldKeys = getIdsFormCustomKey(key);

    if (fieldKeys) {
      if (
        fieldKeys.formType == ServiceFormType.SINGLE_LINE_DESCRIPTION ||
        fieldKeys.formType === ServiceFormType.MULTI_LINE_DESCRIPTION
      ) {
        custom_form_options.push({
          service_custom_form_id: fieldKeys.serviceCustomFormId,
          service_form_id: fieldKeys.serviceFormId,
          value: value,
        });
      }
      if (fieldKeys.formType === ServiceFormType.OPTIONS) {
        custom_form_options.push({
          service_custom_form_id: fieldKeys.serviceCustomFormId,
          service_form_id: fieldKeys.serviceFormId,
          selects: (value as FormCustomFormCustomFormsOptionsInner[]).map(
            (item) => ({
              option_id: item.id,
              value: item.value,
              label:
                item.value === CUSTOM_OTHER_FIELD_ID
                  ? item.otherLabel || ""
                  : item.label,
            }),
          ),
        });
      }
    }
  }

  return {
    // todo アンケート (questionnaire) を追加 + sub_emailを埋めるか削除する
    // questionnaire: customerInputs.questionnaire,
    last_name: customerInputs.lastName,
    first_name: customerInputs.firstName,
    last_name_kana: customerInputs.lastNameKana,
    first_name_kana: customerInputs.firstNameKana,
    company_name: customerInputs.companyName,
    email: customerInputs.email || "",
    sub_email: "",
    send_mail_flag:
      customerInputs.mailMagazine !== undefined
        ? customerInputs.mailMagazine
          ? CustomerSendMailFlag.TARGET
          : CustomerSendMailFlag.NOT_TARGET
        : undefined,
    tel: customerInputs.tel,
    zip: customerInputs.zip1,
    pref_master_id: customerInputs.prefectureId,
    address1: customerInputs.address1,
    birthday: customerInputs.birthday
      ? format(new Date(customerInputs.birthday), "yyyy-MM-dd")
      : undefined,
    age: customerInputs.age ? Number(customerInputs.age) : undefined,
    gender: customerInputs.gender,
    line_id: customerInputs.lineId,
    facebook_id: customerInputs.facebookId,
    twitter_id: customerInputs.twitterId,
    instagram_id: customerInputs.instagramId,
    industry_id: customerInputs.industryId,
    industry_text: customerInputs.industryOther,
    department: customerInputs.department,
    official_position: customerInputs.position,
    hp: customerInputs.companyHp,
    custom_form_options:
      custom_form_options.length > 0 ? custom_form_options : undefined,
    files: customerInputs.files?.map((item) => item.id),
  };
};

/**
 * 期限時刻以前かどうか (フォーム表示の時にも使用するため、export)
 * @param limitTime 受付期限時刻
 * @param currentTime 現在時刻：指定しなかった場合、現在時刻を関数内で取得
 * @returns
 */
export const isBeforeTimeLimit = (
  limitTime: Date,
  currentTime?: Date,
): boolean => {
  const time = currentTime ?? new Date();
  return limitTime > time;
};

const isFullOccupancy = (serviceInfo: ServiceInfo) => {
  if (serviceInfo.type === "subscription") {
    return serviceInfo.details.every((detail) => detail.remainingReservationCount !== undefined && detail.remainingReservationCount <= 0);
  }

  if (serviceInfo.type === "event-seminar") {
    // 詳細項目が期間内か判定のため、現在時刻を取得(満席じゃなくても期間外の場合申し込めないので)
    const currentTime = new Date();
    return serviceInfo.details.every((detail) => {
      if (detail?.eventStartAt) {
        return (
          !isBeforeTimeLimit(detail?.eventStartAt, currentTime) ||
          detail.remainingReservationCount === 0
        );
      }
      return detail.remainingReservationCount === 0;
    });
  }
  return false;
};

/**
 * 予約受付期間外かどうか(期間外の時、trueを返す)
 * @param serviceInfo
 * @returns
 */
const isWithoutReservation = (serviceInfo: ServiceInfo) => {
  // 今が期間内か判定のため、現在時刻を取得
  const currentTime = new Date();
  if (serviceInfo.type !== "event-seminar") {
    // 予約期間内かチェック
    if (
      serviceInfo.reservation_specified_period_flag ===
        ServiceReservationSpecifiedPeriodFlag.SPECIFIED &&
      serviceInfo.reservation_period_start_time &&
      serviceInfo.reservation_period_end_time
    ) {
      // 予約期間が設定されている場合、その期間内か
      const startTime = new Date(serviceInfo.reservation_period_start_time);
      const endTime = new Date(serviceInfo.reservation_period_end_time);
      endTime.setSeconds(59); // 終了時刻の設定は分単位なので、秒数を59とする
      return startTime > currentTime || endTime < currentTime;
    }
    return false;
  }

  // 予約受付フラグ:受付なし
  if (
    serviceInfo.reservation_period_flag ===
    ServiceReservationPeriodFlag.WITHOUT_RESERVATION
  ) {
    return true;
  }

  // 開催前までかどうかチェック
  if (serviceInfo.reserveStatus === ServiceReserveStatus.ALL) {
    // 「全ての日程に参加する」だった場合
    // 期間"外"の詳細項目が一つでもあれば、期間外とする
    if (
      serviceInfo.details.find((detail) =>
        detail.eventStartAt
          ? !isBeforeTimeLimit(detail.eventStartAt, currentTime)
          : false,
      )
    ) {
      return true;
    }
  } else if (
    serviceInfo.details.every((detail) =>
      detail.eventStartAt
        ? !isBeforeTimeLimit(detail.eventStartAt, currentTime)
        : false,
    )
  ) {
    // 全ての詳細項目が期間"外"の場合、期間外とする
    return true;
  }

  // 予約期間内かチェック
  if (
    serviceInfo.reservation_specified_period_flag ===
      ServiceReservationSpecifiedPeriodFlag.SPECIFIED &&
    serviceInfo.reservation_period_start_time &&
    serviceInfo.reservation_period_end_time
  ) {
    // 予約期間が設定されている場合、その期間内か
    const startTime = new Date(serviceInfo.reservation_period_start_time);
    const endTime = new Date(serviceInfo.reservation_period_end_time);
    endTime.setSeconds(59); // 終了時刻の設定は分単位なので、秒数を59とする
    return startTime > currentTime || endTime < currentTime;
  }

  // 全てのチェックをクリアした場合、期間内とする
  return false;
};

const cannotOrderReasonsAtom = atom<string[]>((get) => {
  const formUiState = get(serviceFormUiStateAtom);
  if (formUiState.status !== "Loaded") {
    return [];
  }

  const service = formUiState.formData.service;
  if (
    service.type === "event-seminar" &&
    service.status === ServiceStatus.CLOSE
  ) {
    return ["現在終了の為、申し込みできません"];
  }

  if (isWithoutReservation(service)) {
    return ["受付期間外により申込みできません"];
  }

  if (isFullOccupancy(service)) {
    return ["現在満席の為、申し込みできません"];
  }

  // Add validation error for event-seminar
  // if (
  //   service.type === "event-seminar" &&
  //   formUiState.errors.service.validationError
  // ) {
  //   return [formUiState.errors.service.validationError];
  // }

  return [];
});

const isAgreePolicyAtom = atom((get) => {
  const formUiState = get(serviceFormUiStateAtom);
  if (formUiState.status !== "Loaded") {
    return false;
  }
  return !!formUiState.isAgreePolicy;
});

export const useCannotOrderReasons = (): string[] =>
  useAtomValue(cannotOrderReasonsAtom);

export const useIsAgreePolicy = () => useAtomValue(isAgreePolicyAtom);
